import React, { useCallback, useMemo, useState } from "react";
import classnames from "classnames";
import equal from "fast-deep-equal/es6";

import { range } from "@formant/core/common/range";
import { duration } from "@formant/core/model/duration";
import { IDeviceTeleopConfiguration } from "@formant/core/model/IDeviceTeleopConfiguration";
import { IRtcStream } from "@formant/core/model/IRtcStream";

import { ITeleopModuleProps } from "..";
import { useStores } from "../../../../context/StoreContext";
// import { deviceStore, operateStore } from "../../../../stores";
import { useResizeWatcher } from "../../../../utils/useResizeWatcher";
import { TabPagination } from "../../../common/TabPagination";
import * as styles from "./index.scss";
import { TeleopCommandButton } from "./TeleopCommandButton";

const getComputedStyle = window.getComputedStyle;
export interface ITeleopCommandsProps extends ITeleopModuleProps {
    configuration: IDeviceTeleopConfiguration;
}

function convertRemToPixels(rem: number) {
    return (
        rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
    );
}

const gridGap = convertRemToPixels(0.625);
const boxModelKeys = ["margin", "padding"];
const boxModelPositions = ["top", "right", "bottom", "left"];

const getBoxModel = (element: Element) => {
    const boxModel: Record<string, number> = {};
    const ref = getComputedStyle(element);
    boxModelKeys.forEach(key => {
        boxModelPositions.forEach(position => {
            const pos = position.charAt(0).toUpperCase() + position.slice(1);
            const boxModelKey = `${key}${pos}`;
            boxModel[boxModelKey] = Number(
                // @ts-expect-error: not sure what's going on here…
                parseFloat(ref[boxModelKey]),
            );
        });
    });
    return boxModel;
};

const getButtonDimensions = (current: HTMLDivElement) => {
    const buttons = current?.querySelectorAll(
        '[data-testid="teleop-command-button"]',
    );

    if (buttons.length === 0 && !buttons[0]) {
        return;
    }

    let { height, width } = buttons[0].getBoundingClientRect();

    const {
        marginTop,
        marginBottom,
        paddingTop,
        paddingBottom,
        marginLeft,
        marginRight,
        paddingLeft,
        paddingRight,
    } = getBoxModel(buttons[0]);
    // add in padding / margin / gridgaps
    height += gridGap;
    width += gridGap;
    height +=
        Number(marginTop) +
        Number(marginBottom) +
        Number(paddingTop) +
        Number(paddingBottom);
    width +=
        Number(marginLeft) +
        Number(marginRight) +
        Number(paddingLeft) +
        Number(paddingRight);
    return { height, width };
};

export function Teleop2Commands(props: ITeleopCommandsProps) {
    const { configuration, className, session, streams: maybeStreams } = props;
    const { operateStore, deviceStore } = useStores();
    const bounceSize = 500;
    const [visibleButtons, setVisibleButtons] = useState<number>(bounceSize);
    const [lastDimensions, setLastDimensions] = useState<{
        width: number;
        height: number;
    }>();

    const { ref: handle } = useResizeWatcher<HTMLDivElement, Element>(
        useCallback(
            (contentElementDimensions, current: HTMLDivElement) => {
                if (!contentElementDimensions) {
                    return;
                }
                if (
                    equal(lastDimensions, {
                        width: contentElementDimensions.width,
                        height: contentElementDimensions.height,
                    })
                ) {
                    return;
                }
                const { width, height } = contentElementDimensions;
                const buttonDimensions = getButtonDimensions(current);
                if (buttonDimensions === undefined) {
                    return;
                }
                const w = Math.ceil(width / buttonDimensions.width);
                const h = Math.floor((height - 45) / buttonDimensions.height);
                const maxButtons = w * h;
                setVisibleButtons(maxButtons);
                setLastDimensions({ width, height });
            },
            [lastDimensions],
        ),
        {
            wait: (1 / 4) * duration.second,
            resizeOnMount: true,
            selector: useCallback(
                (module: HTMLDivElement) =>
                    module.closest('[class^="content"]'),
                [],
            ),
        },
    );

    if (visibleButtons === 0) {
        return null;
    }

    const streams: IRtcStream[] = [...(maybeStreams ?? [])];
    const armed =
        operateStore.armed &&
        !operateStore.editingDevice &&
        !operateStore.feedbackVisible &&
        !deviceStore.issuingCommand &&
        !operateStore.usingTerminal;

    const pages = useMemo(() => {
        const tabCount: number = Math.ceil(streams.length / visibleButtons);
        return range(0, tabCount).map(i => {
            const start = i * visibleButtons;
            const end = start + visibleButtons;
            const buttons = streams.slice(start, end).map((stream, index) => {
                const rosStatusTopic = (configuration.rosStreams || []).find(
                    _ => stream.streamName === _.topicName,
                )?.statusTopic;
                const buttonColor = (configuration.rosStreams || []).find(
                    _ => stream.streamName === _.topicName,
                )?.buttonColor;

                const customButtonsTopic =
                    stream.streamName === "Buttons"
                        ? stream.streamName
                        : undefined;

                return (
                    <TeleopCommandButton
                        key={start + index}
                        index={start + index}
                        stream={stream}
                        session={session}
                        disabled={!armed}
                        statusTopic={rosStatusTopic || customButtonsTopic}
                        statusLabel={`${i} ${stream.label}`}
                        backgroundColor={buttonColor}
                    />
                );
            });

            return (
                <div key={i} className={styles.page}>
                    {buttons}
                </div>
            );
        });
    }, [streams, visibleButtons, armed, session]);

    const dom = useMemo(() => {
        const hasPagination = pages.length > 0;
        return (
            <div ref={handle}>
                <TabPagination
                    className={classnames(
                        styles["teleop-commands"],
                        hasPagination && styles.pagination,
                        className,
                    )}
                    pages={pages}
                    position="top"
                />
            </div>
        );
    }, [pages]);

    return dom;
}
